import {
  CustomFieldEntityEnum,
  ICustomFieldRecordSchema,
  IQuoteOfferingRespSchema,
} from '@monetize/types/app';
import { formatInteger, pluralize } from '@monetize/utils/core';
import { BsTrash } from 'react-icons/bs';
import { FaChevronRight } from 'react-icons/fa';
import { useGetAccountById } from '../../../../../api/accountsService';
import { AccountSelectWithHierarchy } from '../../../../../components/Account/AccountSelectWithHierarchy';
import { CustomFieldDataForm } from '../../../../../components/CustomFields/CustomFieldDataForm';
import {
  MBox,
  MCustomIconButton,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { useQuoteOfferingGroupCustomFields } from '../../../../../hooks/useQuoteOfferingGroupCustomFields';
import {
  canMoveQuoteOfferingBetweenGroups,
  isQuoteOfferingGroupAddedOnQuote,
  QuoteOfferingGroupPermissions,
} from '../../../../../utils/quotes';
import { QuoteOfferingGroupCustomFieldsPopover } from '../quoteOfferingGroup/QuoteOfferingGroupCustomFieldsPopover';
import {
  GroupHeaderAccountField,
  GroupHeaderDivider,
} from '../quoteOfferingGroup/QuoteOfferingGroupHeaderShell';
import { ManageQuoteOfferingGroupsChip } from './ManageQuoteOfferingGroupsChip';
import { MANAGE_GROUPS_GRID_TEMPLATE_COLUMNS } from './manageQuoteOfferingGroupsConstants';
import { ManageQuoteOfferingGroupsContactCell } from './ManageQuoteOfferingGroupsContactCell';
import { ManageQuoteOfferingGroupsDropZone } from './ManageQuoteOfferingGroupsDropZone';
import {
  getManageGroupsRowLabel,
  ManageGroupsDraftRow,
  ManageGroupsOfferingDetail,
  ManageGroupsRowStatus,
} from './manageQuoteOfferingGroupsUtils';
import {
  ManageGroupsEditableField,
  ManageGroupsRowFieldErrors,
} from './useManageQuoteOfferingGroupsDraft';

const DELETE_GROUP_TOOLTIP =
  'Deleting this group moves its offerings to Ungrouped. The offerings stay on the quote.';

/** MFormField reads `error.message`, so a bare string has to be wrapped */
const toFieldError = (message?: string) => (message ? { message } : undefined);

/** Stable empty value so the custom field form isn't handed a new object per render */
const NO_CUSTOM_FIELDS: ICustomFieldRecordSchema = {};

/** Matches the `sm` input height so text cells line up with select cells */
const INPUT_HEIGHT = '30px';

/**
 * Repeated on every card rather than once in a table header: with several
 * similar looking selects per row and a list that scrolls, a single header row
 * left people guessing which value was ship-to and which was bill-to.
 */
const CellLabel = ({ children }: { children: string }) => (
  <MText
    fontSize="xs"
    fontWeight="semibold"
    color="fg.muted"
    textTransform="uppercase"
    letterSpacing="0.02em"
  >
    {children}
  </MText>
);

/**
 * Short status word in a card's header strip — the one place a collapsed card
 * can report that it has staged changes or is missing a required value.
 */
const RowFlag = ({ color, children }: { color: string; children: string }) => (
  <MText
    fontSize="xs"
    fontWeight={600}
    color={color}
    textTransform="uppercase"
    letterSpacing="0.02em"
    whiteSpace="nowrap"
  >
    {children}
  </MText>
);

/**
 * Billing account is not editable anywhere today, so it renders as text that can
 * wrap instead of a truncating read-only input. Every row bills to the same
 * account in the common case, so react-query serves them all one request.
 */
const BillingAccountName = ({ accountId }: { accountId: string }) => {
  const { data: account } = useGetAccountById(accountId, {
    enabled: !!accountId,
  });

  return (
    <MFlex minH={INPUT_HEIGHT} align="center">
      <MText fontSize="sm" color="fg.primary">
        {account?.accountName || '-'}
      </MText>
    </MFlex>
  );
};

/**
 * Ship To / Bill To on a collapsed card, rendered as the same account preview
 * popover the quote's own group containers use. The draft carries account ids
 * only, so the name is resolved here; rows share accounts in the common case, so
 * react-query serves the whole list from one request per account.
 */
const HeaderAccountField = ({
  label,
  accountId,
}: {
  label: string;
  accountId: string;
}) => {
  const { data: account } = useGetAccountById(accountId, {
    enabled: !!accountId,
  });

  return (
    <GroupHeaderAccountField
      label={label}
      accountId={accountId}
      accountName={account?.accountName || '-'}
    />
  );
};

interface ManageQuoteOfferingGroupsRowProps {
  row: ManageGroupsDraftRow;
  rowIndex: number;
  rootAccountId: string;
  offeringById: Map<string, IQuoteOfferingRespSchema>;
  offeringDetailById: Map<string, ManageGroupsOfferingDetail>;
  permissions: QuoteOfferingGroupPermissions;
  /** Pre-formatted sum of the row's offerings, ramp segments included */
  subtotal: string;
  /** Undefined when the row matches the server — nothing to flag */
  status?: ManageGroupsRowStatus;
  /** Validation messages keyed by field, rendered under their own input */
  errors?: ManageGroupsRowFieldErrors;
  onUpdateField: (
    key: string,
    field: ManageGroupsEditableField,
    value: string,
  ) => void;
  onAssignOffering: (quoteOfferingId: string, toRowKey: string | null) => void;
  onUpdateCustomFields: (
    key: string,
    customFields: ICustomFieldRecordSchema,
  ) => void;
  onRemoveRow: (key: string) => void;
  /** Collapsed cards keep their header strip, and still accept dropped chips */
  isOpen: boolean;
  onIsOpenChange: (key: string, isOpen: boolean) => void;
}

export const ManageQuoteOfferingGroupsRow = ({
  row,
  rowIndex,
  rootAccountId,
  offeringById,
  offeringDetailById,
  permissions,
  subtotal,
  status,
  errors: rowErrors,
  onUpdateField,
  onAssignOffering,
  onUpdateCustomFields,
  onRemoveRow,
  isOpen,
  onIsOpenChange,
}: ManageQuoteOfferingGroupsRowProps) => {
  const { hasCustomFields } = useQuoteOfferingGroupCustomFields();

  // An id with no live offering behind it (deleted since the draft was seeded)
  // simply doesn't render — the payload sanitizer drops it on save.
  const members = row.quoteOfferingIds.flatMap((quoteOfferingId) => {
    const quoteOffering = offeringById.get(quoteOfferingId);
    return quoteOffering ? [quoteOffering] : [];
  });

  /**
   * Deleting the group and re-pointing its accounts are blocked by the same
   * thing: a group carried over from the quote being amended or renewed belongs
   * to the contract's existing offerings. A row staged in this modal has no
   * reference either, so it stays fully editable and can simply be dropped.
   */
  const canEditGroup =
    !permissions.isReadOnly && isQuoteOfferingGroupAddedOnQuote(row);
  const rowLabel = getManageGroupsRowLabel(rowIndex);
  const testId = `manage-groups-row-${row.key}`;
  // "…is required" under a field nobody can fill in is noise, not guidance
  const errors = permissions.isReadOnly ? undefined : rowErrors;
  // Flagged in the header because a collapsed card hides the field-level
  // messages, and an invalid row anywhere disables Save for the whole modal.
  const hasErrors = !!errors && Object.keys(errors).length > 0;

  return (
    <ManageQuoteOfferingGroupsDropZone
      rowKey={row.key}
      onAssign={onAssignOffering}
      isDropDisabled={permissions.isReadOnly}
      // Clips the header strip's fill to the card's rounded corners
      overflow="hidden"
      // Unsaved rows carry the same 3px orange left border the `unsaved` input
      // variant uses, so "this has pending changes" looks the same app-wide.
      // Explicitly solid: the card's own edge is dashed to mark it as a drop
      // target, and the unsaved marker is solid everywhere else in the app.
      {...(status
        ? {
            borderLeftWidth: '3px',
            borderLeftStyle: 'solid',
            borderLeftColor: 'tOrange.base',
          }
        : {})}
      data-testid={testId}
    >
      {/*
       * Always rendered, so a collapsed card still says which group it is, how
       * many offerings it holds, and whether it needs attention — and still
       * offers its delete action.
       */}
      <MFlex
        alignItems="center"
        gap={3}
        px={3}
        py={2}
        bg="bg.subtle"
        borderBottomWidth={isOpen ? '1px' : undefined}
        borderBottomStyle="solid"
        borderBottomColor="border.subtle"
      >
        <MCustomIconButton
          btnSize={2.5}
          containerSize={6}
          icon={FaChevronRight}
          iconProps={{
            transform: isOpen ? 'rotate(90deg)' : undefined,
            transition: 'all ease 0.3s',
          }}
          // Purpose-based and stable across toggles — the chevron's rotation
          // carries the open/closed state.
          aria-label={`Toggle ${rowLabel}`}
          aria-expanded={isOpen}
          onClick={() => onIsOpenChange(row.key, !isOpen)}
          data-testid={`${testId}-toggle`}
        />
        <MText fontWeight={600} fontSize="sm" whiteSpace="nowrap">
          {rowLabel}
        </MText>
        {/*
         * Kept next to the group number rather than at the end of the run below,
         * so scanning a stack of cards for the ones that were touched reads down
         * a column instead of hunting past account names of varying length.
         */}
        {status && (
          <RowFlag color="tOrange.base">
            {status === 'new' ? 'New' : 'Edited'}
          </RowFlag>
        )}
        {hasErrors && <RowFlag color="tRed.base">Incomplete</RowFlag>}

        {/*
         * Collapsed, the strip is all the card has to identify itself by, so it
         * carries the same Ship To / Bill To account popovers and Additional
         * Fields popover the quote's own group containers use. Expanded, the
         * body below already shows every one of those values — and shows them
         * editable — so the strip stays out of the way.
         *
         * The run wraps rather than truncating the strip: long account names on
         * a narrow card push the offering count to a second line instead of off
         * the edge.
         */}
        <MFlex
          alignItems="center"
          flexWrap="wrap"
          columnGap={3}
          rowGap={1}
          flex="1"
          minW={0}
        >
          {!isOpen && (
            <>
              <GroupHeaderDivider />
              <HeaderAccountField
                label="Ship To"
                accountId={row.shippingAccountId}
              />
              <GroupHeaderDivider />
              <HeaderAccountField
                label="Bill To"
                accountId={row.billingAccountId}
              />
              <GroupHeaderDivider />
            </>
          )}
          <MText fontSize="sm" color="fg.muted" whiteSpace="nowrap">
            {formatInteger(members.length)}{' '}
            {pluralize('offering', members.length)}
          </MText>
          {/* Draft values, not the server's — it has to agree with the body */}
          {!isOpen && (
            <QuoteOfferingGroupCustomFieldsPopover
              customFields={row.customFields}
              groupLabel={rowLabel}
              testId={testId}
            />
          )}
        </MFlex>

        {/*
         * Total then delete, matching the quote's own group container headers.
         * The delete slot holds its width either way, so subtotals line up down
         * a stack of cards whether or not a given row can be deleted.
         */}
        <MFlex flexShrink={0} alignItems="center" gap={1}>
          <MText
            fontWeight="bold"
            whiteSpace="nowrap"
            data-testid={`${testId}-subtotal`}
          >
            {subtotal}
          </MText>
          <MFlex minW={6} justifyContent="flex-end">
            {canEditGroup && (
              <MTooltip label={DELETE_GROUP_TOOLTIP} placement="top-end">
                <MCustomIconButton
                  btnSize={4}
                  containerSize={6}
                  icon={BsTrash}
                  aria-label={`Delete ${rowLabel}. ${DELETE_GROUP_TOOLTIP}`}
                  onClick={() => onRemoveRow(row.key)}
                />
              </MTooltip>
            )}
          </MFlex>
        </MFlex>
      </MFlex>

      {isOpen && (
        <MBox px={4} py={3}>
          <MGrid
            templateColumns={MANAGE_GROUPS_GRID_TEMPLATE_COLUMNS}
            columnGap={4}
            alignItems="start"
          >
            <MFlex direction="column" gap={2}>
              <CellLabel>Ship To</CellLabel>
              <MFormField
                label="Account"
                error={toFieldError(errors?.shippingAccountId)}
              >
                <AccountSelectWithHierarchy
                  rootAccountId={rootAccountId}
                  value={row.shippingAccountId}
                  onChange={(accountId) =>
                    onUpdateField(row.key, 'shippingAccountId', accountId)
                  }
                  onClearAccountValue={() =>
                    onUpdateField(row.key, 'shippingAccountId', '')
                  }
                  clearable={canEditGroup}
                  isReadOnly={!canEditGroup}
                />
              </MFormField>
              <MFormField
                label="Contact"
                error={toFieldError(errors?.shippingContactId)}
              >
                <ManageQuoteOfferingGroupsContactCell
                  accountId={row.shippingAccountId}
                  contactId={row.shippingContactId}
                  placeholder="Select Contact"
                  isReadOnly={permissions.isReadOnly}
                  onChange={(contactId) =>
                    onUpdateField(row.key, 'shippingContactId', contactId)
                  }
                />
              </MFormField>
            </MFlex>

            <MFlex direction="column" gap={2}>
              <CellLabel>Bill To</CellLabel>
              <MFormField
                label="Account"
                error={toFieldError(errors?.billingAccountId)}
              >
                <BillingAccountName accountId={row.billingAccountId} />
              </MFormField>
              <MFormField
                label="Contact"
                error={toFieldError(errors?.billingContactId)}
              >
                <ManageQuoteOfferingGroupsContactCell
                  accountId={row.billingAccountId}
                  contactId={row.billingContactId}
                  placeholder="Select Contact"
                  isReadOnly={permissions.isReadOnly}
                  onChange={(contactId) =>
                    onUpdateField(row.key, 'billingContactId', contactId)
                  }
                />
              </MFormField>
            </MFlex>

            <MFlex direction="column" gap={2}>
              <CellLabel>Offerings</CellLabel>
              {members.length === 0 ? (
                <MText color="fg.muted" fontSize="xs">
                  This group does not have any offerings.
                </MText>
              ) : (
                <MFlex direction="column" gap={2}>
                  {members.map((quoteOffering) => {
                    const isMovable = canMoveQuoteOfferingBetweenGroups(
                      quoteOffering,
                      permissions,
                    );
                    return (
                      <ManageQuoteOfferingGroupsChip
                        key={quoteOffering.id}
                        quoteOffering={quoteOffering}
                        detail={offeringDetailById.get(quoteOffering.id)}
                        rowKey={row.key}
                        isMovable={isMovable}
                        onUngroup={() =>
                          onAssignOffering(quoteOffering.id, null)
                        }
                      />
                    );
                  })}
                </MFlex>
              )}
            </MFlex>
          </MGrid>

          {/*
           * Full card width below the columns, laid out inline rather than
           * behind an accordion — the whole card collapses now, so a second
           * disclosure inside it was one click too many. Gated on the tenant
           * actually having group custom fields, since the form itself renders
           * nothing when there are none and would leave a stray heading.
           */}
          {hasCustomFields && (
            <MBox mt={4}>
              <MDivider mb={3} />
              <CellLabel>Additional Fields</CellLabel>
              <CustomFieldDataForm
                entity={CustomFieldEntityEnum.QUOTE_OFFERING_GROUP}
                mode="drawer"
                value={row.customFields ?? NO_CUSTOM_FIELDS}
                setValue={(customFields) =>
                  onUpdateCustomFields(row.key, customFields)
                }
                isReadOnly={permissions.isReadOnly}
                fieldColSpan={3}
              />
            </MBox>
          )}
        </MBox>
      )}
    </ManageQuoteOfferingGroupsDropZone>
  );
};
